import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check, Zap, Flame, ArrowRight, ChevronDown,
  Target,                               // Profit target only
  TrendingDown,                         // Daily Loss only
  ShieldAlert,                          // Max Drawdown only
  Percent,                              // Account Gains Share only
  CalendarClock,                        // Payout Frequency only
  Scale,                                // Leverage only
  ShieldCheck,                          // Payout Protector add-on
  CalendarDays,                         // Weekend Hold add-on
  Plus,                                 // generic add-on marker
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { CHALLENGES, PLATFORMS, buyUrl, sizeKey } from "@/data/site";
import { PROGRAM_BULLETS, PROGRAM_STATS, PROGRAM_PERKS } from "@/data/challenges-program-content";

/* StepBadge — renders a literal "1" or "2" character as the tab
   "icon". Cleaner and more intuitive than abstract step-themed
   lucide icons that nobody recognises. */
function StepBadge({ n }) {
  return (
    <span className="font-display font-semibold text-[15px] leading-none tabular-nums">
      {n}
    </span>
  );
}

/* ICON MAP (single source of truth — every concept gets exactly one icon)
   Tab          icon
   Instant      Zap
   1-Step       Footprints
   2-Step       Workflow

   Bullet                       icon
   Funded from day one          Rocket
   One eval to pass             ListChecks
   Two phases                   GitMerge
   No time limit                InfinityIcon
   Hold overnight & weekends    Moon
   Lowest entry price           Tag
   Up to 90% gain share         Percent

   Rule highlight              icon
   Profit Target                Target
   Daily Loss                   TrendingDown
   Max Drawdown                 ShieldAlert
   Account Gains Share          Percent       (matches bullet — same concept)
   Payout Frequency             CalendarClock
   Leverage                     Scale         */

/* Animated price counter — tweens between values on size/program change. */
function AnimatedPrice({ value }) {
  const [shown, setShown] = useState(value);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const from = shown;
    const to = value;
    const duration = 360;
    const step = (t) => {
      const e = Math.min(1, (t - start) / duration);
      const k = 1 - Math.pow(1 - e, 3);
      setShown(Math.round(from + (to - from) * k));
      if (e < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <>${shown.toLocaleString()}</>;
}

/* Highlight rows for the middle column — 6 high-signal rules per
   program. Icons are unique to each concept (see ICON MAP). */
function summariseRules(programId, rules, t) {
  const find = (area) => rules.find((r) => r.area === area) || {};
  const target = find("Equity Growth Target");
  const daily = find("Daily Loss Limit");
  const maxdd = find("Max Drawdown");
  const lev = find("Leverage");

  const targetVal = programId === "instant"
    ? t("builder.rules.values.none", "None")
    : programId === "oneStep"
      ? target.assessment || "10%"
      : `${target.step1 || "8%"} → ${target.step2 || "5%"}`;

  return [
    { Icon: Target,        label: t("builder.rules.profit_target", "Profit Target"),       value: targetVal,
      note: programId === "instant"
        ? t("builder.rules.notes.funded_day_one", "Funded from day one")
        : programId === "twoStep"
          ? t("builder.rules.notes.phase_transition", "Phase 1 → Phase 2")
          : t("builder.rules.notes.single_phase", "Single phase") },
    { Icon: TrendingDown,  label: t("builder.rules.daily_loss", "Daily Loss"),             value: daily.funded || "5%",
      note: t("builder.rules.notes.resets_daily", "Resets daily") },
    { Icon: ShieldAlert,   label: t("builder.rules.max_drawdown", "Max Drawdown"),         value: maxdd.funded || "8%",
      note: programId === "instant"
        ? t("builder.rules.notes.trailing_lock", "Trailing lock")
        : t("builder.rules.notes.static", "Static") },
    { Icon: Percent,       label: t("builder.rules.account_gains_share", "Account Gains Share"),
      value: programId === "instant" ? "80–90%" : t("builder.rules.values.up_to_90", "Up to 90%"),
      note: t("builder.rules.notes.tier_based", "Tier-based") },
    { Icon: CalendarClock, label: t("builder.rules.payout_frequency", "Payout Frequency"),
      value: t("builder.rules.values.monthly", "Monthly"),
      note: t("builder.rules.notes.on_demand", "On-demand") },
    { Icon: Scale,         label: t("builder.rules.leverage", "Leverage"),                value: lev.funded || lev.assessment || "1:50",
      note: t("builder.rules.notes.forex_metals", "Forex & metals") },
  ];
}

export default function ChallengesSection() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const PROGRAM_TABS = [
    // Instant ⚡ uses amber/gold (distinct identity, NOT brand cyan)
    // so it visually separates from all the other ⚡ icons on the
    // page that mean "speed of execution" or "fast onboarding".
    { id: "instant", label: t("builder.programs.instant.label", "Instant Funding"),  shortLabel: t("builder.programs.instant.shortLabel", "Instant"), sub: t("builder.programs.instant.sub", "Funded from day one"),     IconEl: () => <Zap size={16} strokeWidth={2.2} fill="#f59e0b" style={{ color: "#f59e0b" }} /> },
    { id: "oneStep", label: t("builder.programs.oneStep.label", "1-Step Challenge"), shortLabel: t("builder.programs.oneStep.shortLabel", "1-Step"),  sub: t("builder.programs.oneStep.sub", "Single eval, fast track"), IconEl: () => <StepBadge n="1" /> },
    { id: "twoStep", label: t("builder.programs.twoStep.label", "2-Step Challenge"), shortLabel: t("builder.programs.twoStep.shortLabel", "2-Step"),  sub: t("builder.programs.twoStep.sub", "Two phases, lowest cost"), IconEl: () => <StepBadge n="2" /> },
  ];

  const initialProgram = ["oneStep", "twoStep", "instant"].includes(searchParams.get("p"))
    ? searchParams.get("p")
    : "twoStep";
  const [program, setProgram] = useState(initialProgram);
  const active = CHALLENGES[program];

  const initialSize = (() => {
    const requested = parseInt(searchParams.get("s") || "", 10);
    const fmt = `$${requested.toLocaleString()}`;
    if (active.sizes.includes(fmt)) return fmt;
    return active.sizes[1] || active.sizes[0];
  })();
  const [size, setSize] = useState(initialSize);
  const [platformId, setPlatformId] = useState("ctrader");

  // Add-ons — different per program (sourced from official WL2 +
  // Instant Funded intake forms).
  //   • Instant Funded: 90% Profit Split (+20%) upgrades base 80% → 90%
  //                     Weekend Hold (+10%) lets traders hold over weekend
  //   • 1-Step & 2-Step: Payout Protector (+25%) keeps gains on a breach
  //                      Remove Lock Upon Payout (+25%) removes the standard
  //                      lock that normally pauses an account post-payout
  const ADDONS_BY_PROGRAM = {
    instant: [
      {
        id: "profitSplit90",
        Icon: Percent,
        label: t("builder.addons.profitSplit90.label", "90% Profit Split"),
        blurb: t("builder.addons.profitSplit90.blurb", "Upgrade gain share from 80% → 90%"),
        pct: 0.20,
      },
      {
        id: "weekendHold",
        Icon: CalendarDays,
        label: t("builder.addons.weekendHold.label", "Weekend Hold"),
        blurb: t("builder.addons.weekendHold.blurb", "Hold positions into Monday open"),
        pct: 0.10,
      },
    ],
    oneStep: [
      {
        id: "payoutProtector",
        Icon: ShieldCheck,
        label: t("builder.addons.payoutProtector.label", "Payout Protector"),
        blurb: t("builder.addons.payoutProtector.blurb", "Keep your gains even on a breach"),
        pct: 0.25,
      },
      {
        id: "removeLock",
        Icon: Zap,
        label: t("builder.addons.removeLock.label", "Remove Lock Upon Payout"),
        blurb: t("builder.addons.removeLock.blurb", "Skip the standard post-payout lock"),
        pct: 0.25,
      },
    ],
    twoStep: [
      {
        id: "payoutProtector",
        Icon: ShieldCheck,
        label: t("builder.addons.payoutProtector.label", "Payout Protector"),
        blurb: t("builder.addons.payoutProtector.blurb", "Keep your gains even on a breach"),
        pct: 0.25,
      },
      {
        id: "removeLock",
        Icon: Zap,
        label: t("builder.addons.removeLock.label", "Remove Lock Upon Payout"),
        blurb: t("builder.addons.removeLock.blurb", "Skip the standard post-payout lock"),
        pct: 0.25,
      },
    ],
  };
  const ADDONS = ADDONS_BY_PROGRAM[program] || [];

  // Add-ons persist in the URL as ?a=id1,id2 so refresh/share/back-nav
  // restores the user's selection. Parse the initial state once on mount.
  const initialAddons = (() => {
    const raw = (searchParams.get("a") || "").split(",").filter(Boolean);
    const valid = ADDONS_BY_PROGRAM[initialProgram]?.map((a) => a.id) || [];
    const m = {};
    for (const id of raw) if (valid.includes(id)) m[id] = true;
    return m;
  })();
  const [addons, setAddons] = useState(initialAddons);

  // Toggle helper — mutates state AND syncs the `a` query param so the
  // current build (program + size + addons) is fully encoded in the URL.
  const toggleAddon = (id) => {
    setAddons((m) => {
      const next = { ...m, [id]: !m[id] };
      const enabled = Object.keys(next).filter((k) => next[k]);
      const params = new URLSearchParams(searchParams);
      if (enabled.length) params.set("a", enabled.join(","));
      else params.delete("a");
      setSearchParams(params, { replace: true });
      return next;
    });
  };
  const [rulesExpanded, setRulesExpanded] = useState(false);

  useEffect(() => {
    const p = searchParams.get("p");
    if (p && ["oneStep", "twoStep", "instant"].includes(p)) {
      // Even if program already matches, re-scroll & re-sync size so
      // repeat clicks on the same mega-menu item still snap into view.
      if (p !== program) setProgram(p);
      const requested = parseInt(searchParams.get("s") || "", 10);
      const fmt = `$${requested.toLocaleString()}`;
      const sizes = CHALLENGES[p].sizes;
      const nextSize = sizes.includes(fmt) ? fmt : sizes[1] || sizes[0];
      if (nextSize !== size) setSize(nextSize);

      // Restore add-on selection from `a=id1,id2` if present. We only
      // accept ids that belong to the CURRENT program — anything else
      // is dropped silently (prevents stale ids from another program).
      const validIds = (ADDONS_BY_PROGRAM[p] || []).map((x) => x.id);
      const requested_a = (searchParams.get("a") || "").split(",").filter(Boolean);
      const nextAddons = {};
      for (const id of requested_a) if (validIds.includes(id)) nextAddons[id] = true;
      setAddons(nextAddons);

      requestAnimationFrame(() => {
        document.getElementById("challenges")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const switchProgram = (id) => {
    setProgram(id);
    setSize(CHALLENGES[id].sizes[1] || CHALLENGES[id].sizes[0]);
    setAddons({});
    setSearchParams({ p: id }, { replace: true });
  };

  const prices = active.pricing[size];
  const basePrice = prices?.[platformId] || 0;
  const addonsTotal = ADDONS.reduce((s, a) => s + (addons[a.id] ? Math.round(basePrice * a.pct) : 0), 0);
  const currentPrice = basePrice + addonsTotal;

  // Active platform record — drives the right card's accent colour.
  const activePlatform = PLATFORMS.find((p) => p.id === platformId) || PLATFORMS[0];

  const summary = useMemo(() => summariseRules(program, active.rules, t), [program, active, t]);

  const programBullets = PROGRAM_BULLETS[program];
  const programStats = PROGRAM_STATS[program];
  const programPerks = PROGRAM_PERKS[program];

  return (
    <section id="challenges" data-testid="challenges-section" className="relative py-14 lg:py-32 overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-5 lg:px-8">
        <div className="text-center mb-8 lg:mb-10">
          <div className="pill-mono inline-flex mb-4">{t("builder.pill", "CHALLENGE BUILDER")}</div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-5xl font-medium tracking-tight mb-3">
            {t("builder.title1", "Choose the challenge that matches your")} <span className="text-gradient-brand font-semibold">{t("builder.titleAccent", "performance style")}</span>
          </h2>
          <p className="text-[color:var(--text-2)] max-w-2xl mx-auto text-[13px] sm:text-sm lg:text-base font-light">
            {t("builder.subtitle", "Scalper, swing trader, or somewhere in between? Build an evaluation that lets your performance speak for itself.")}
          </p>
        </div>

        {/* Big pill toggles — competitor-style flashy program switcher */}
        <div
          className="grid grid-cols-3 gap-2 sm:gap-3 mb-6 max-w-3xl mx-auto"
          data-testid="program-tabs"
        >
          {PROGRAM_TABS.map((tab) => {
            const isActive = program === tab.id;
            const isPopular = tab.id === "twoStep";
            return (
              <div key={tab.id} className="relative">
                {isPopular && (
                  <span
                    data-testid="program-tab-popular-badge"
                    className="badge-popular pointer-events-none absolute -top-2 -right-1 sm:-top-2.5 sm:-right-2 inline-flex items-center gap-1 px-2 sm:px-2.5 py-0.5 rounded-full text-[10px] sm:text-[10px] font-semibold uppercase tracking-wider z-20 whitespace-nowrap"
                    style={{
                      background: "linear-gradient(135deg, var(--brand) 0%, var(--brand-deep) 100%)",
                      color: "#071116",
                      boxShadow: "0 4px 14px color-mix(in srgb, var(--brand) 35%, transparent)",
                    }}
                  >
                    <Flame size={9} strokeWidth={2.6} />
                    {t("builder.popular", "Popular")}
                  </span>
                )}
                <button
                  data-testid={`program-tab-${tab.id}`}
                  onClick={() => switchProgram(tab.id)}
                  className={`relative group w-full rounded-2xl py-2.5 px-2 sm:px-3 sm:py-3 lg:px-5 lg:py-4 transition-all overflow-hidden ${isActive ? "race-border race-border-strong" : ""}`}
                  style={{
                    background: isActive
                      ? "linear-gradient(135deg, color-mix(in srgb, var(--brand) 18%, var(--surface)) 0%, var(--surface) 100%)"
                      : "var(--surface-3)",
                    boxShadow: isActive
                      ? "0 0 0 1px color-mix(in srgb, var(--brand) 55%, transparent), 0 8px 28px color-mix(in srgb, var(--brand) 18%, transparent)"
                      : "0 0 0 1px var(--line)",
                  }}
                >
                  {/* Mobile: stack icon over label centred. Desktop: row */}
                  <div className="flex flex-col sm:flex-row items-center sm:items-center sm:text-left gap-1 sm:gap-2.5 min-w-0">
                    <span
                      className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg grid place-items-center shrink-0 transition-colors"
                      style={{
                        background: isActive ? "color-mix(in srgb, var(--brand) 22%, transparent)" : "var(--line)",
                        color: isActive ? "var(--brand)" : "var(--text-2)",
                      }}
                    >
                      <tab.IconEl />
                    </span>
                    <span className="min-w-0 flex-1 text-center sm:text-left">
                      <span
                        className={`block font-display text-[12px] sm:text-sm lg:text-base font-medium leading-tight truncate ${isActive ? "text-[color:var(--text-1)]" : "text-[color:var(--text-2)]"}`}
                      >
                        <span className="sm:hidden">{tab.shortLabel}</span>
                        <span className="hidden sm:inline">{tab.label}</span>
                      </span>
                      <span className="hidden sm:block text-[12px] lg:text-[11px] text-[color:var(--text-3)] font-light truncate">{tab.sub}</span>
                    </span>
                  </div>
                </button>
              </div>
            );
          })}
        </div>

        {/* Account size pills — one continuous rounded container */}
        <div
          className="mx-auto mb-8 flex items-center justify-between gap-1 p-1 rounded-full max-w-2xl overflow-x-auto scrollbar-hidden"
          data-testid="size-pills"
          style={{ background: "var(--surface-3)", border: "1px solid var(--line)" }}
        >
          {active.sizes.map((s) => {
            const isActive = size === s;
            return (
              <button
                key={s}
                data-testid={`size-pill-${s.replace(/[$,]/g, "")}`}
                onClick={() => {
                  setSize(s);
                  // Reflect the new size in the URL so the full build
                  // (program + size + addons) stays shareable.
                  const params = new URLSearchParams(searchParams);
                  params.set("s", s.replace(/[$,]/g, ""));
                  setSearchParams(params, { replace: true });
                }}
                className={`flex-1 px-3 sm:px-4 py-2 rounded-full font-num text-[12px] sm:text-sm tracking-tight whitespace-nowrap transition-all ${
                  isActive
                    ? "bg-brand text-[#071116] font-semibold"
                    : "text-[color:var(--text-2)] hover:text-[color:var(--text-1)]"
                }`}
                style={isActive ? { boxShadow: "0 1px 8px color-mix(in srgb, var(--brand) 35%, transparent)" } : undefined}
              >
                {sizeKey(s)}
              </button>
            );
          })}
        </div>

        {/* 3-column challenge builder — left features, middle rules, right flashy price card.
            The layout is persistent (no AnimatePresence wrap). Values inside the
            columns update in place — AnimatedPrice tweens the number, and we use
            framer-motion's layout/keyed-fade-in on the changing text nodes for a
            smooth feel without the previous "card flash" effect. */}
        <div className="grid lg:grid-cols-12 gap-4 lg:gap-5">
            {/* LEFT — program features */}
            <div
              data-testid="challenge-features-col"
              className="order-1 lg:col-span-4 card-beam p-5 lg:p-7 rounded-2xl flex flex-col lg:min-h-[560px]"
              style={{ border: "1px solid color-mix(in srgb, var(--brand) 16%, transparent)" }}
            >
              <div className="pill-mono inline-flex mb-3 self-start">{active.label}</div>
              {/* Title + tagline crossfade smoothly on program switch — no
                  big card flash, just a 180ms text morph. */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={program}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18 }}
                >
                  <h3 className="font-display text-2xl lg:text-3xl font-medium text-[color:var(--text-1)] mb-2">
                    {PROGRAM_TABS.find((t) => t.id === program).label}
                  </h3>
                  <p className="text-[color:var(--text-3)] text-sm font-light mb-5">
                    {PROGRAM_TABS.find((t) => t.id === program).sub}.
                  </p>
                </motion.div>
              </AnimatePresence>

              {/* Bullets stagger-fade between programs */}
              <AnimatePresence mode="wait">
                <motion.ul
                  key={program}
                  className="space-y-2.5 mb-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.16 }}
                >
                  {programBullets.map((b, idx) => (
                    <motion.li
                      key={b.key}
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.18, delay: idx * 0.04 }}
                      className="flex items-center gap-2.5 text-sm text-[color:var(--text-2)]"
                    >
                      <span
                        className="w-7 h-7 rounded-lg grid place-items-center shrink-0"
                        style={{
                          background: "color-mix(in srgb, var(--brand) 10%, transparent)",
                          border: "1px solid color-mix(in srgb, var(--brand) 24%, transparent)",
                        }}
                      >
                        <b.Icon size={13} className="text-brand" />
                      </span>
                      <span>{t(b.key)}</span>
                    </motion.li>
                  ))}
                </motion.ul>
              </AnimatePresence>

              {/* Key-spec stat grid — 2×2 numeric headlines that fill
                  the gap between bullets and the platform picker and
                  give the column a strong, data-rich centrepiece. */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={`stats-${program}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  data-testid="program-stats-grid"
                  className="grid grid-cols-2 gap-2 mb-2"
                >
                  {programStats.map((s) => (
                    <div
                      key={s.labelKey}
                      className="rounded-xl p-3 lg:p-3.5"
                      style={{
                        background: "color-mix(in srgb, var(--brand) 5%, var(--surface-2))",
                        border: "1px solid color-mix(in srgb, var(--brand) 14%, transparent)",
                      }}
                    >
                      <div className="font-display text-[15px] lg:text-[17px] font-semibold text-[color:var(--text-1)] tabular-nums leading-tight">
                        {s.valueKey ? t(s.valueKey) : s.value}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)] font-medium mt-1 leading-tight">
                        {t(s.labelKey)}
                      </div>
                    </div>
                  ))}
                </motion.div>
              </AnimatePresence>

              <div className="mt-auto pt-4 border-t border-[color:var(--line)]/60">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--text-3)] font-semibold mb-3 flex items-center gap-2">
                  {t("builder.choose_platform", "Choose your platform")}
                  <span className="text-[12px] normal-case tracking-normal text-brand font-medium">{t("builder.click_to_switch", "click to switch price")}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {PLATFORMS.map((p) => {
                    const isActive = platformId === p.id;
                    return (
                      <button
                        key={p.id}
                        onClick={() => setPlatformId(p.id)}
                        data-testid={`feature-platform-${p.id}`}
                        aria-pressed={isActive}
                        aria-label={`Select ${p.name} platform`}
                        className="relative flex items-center gap-2 px-3 py-2.5 rounded-xl transition-all"
                        style={{
                          background: isActive
                            ? `linear-gradient(135deg, color-mix(in srgb, ${p.accent} 22%, var(--surface-2)) 0%, var(--surface-2) 100%)`
                            : "var(--surface-2)",
                          border: isActive
                            ? `1.5px solid ${p.accent}`
                            : "1px solid color-mix(in srgb, var(--brand) 14%, transparent)",
                          boxShadow: isActive
                            ? `0 0 0 3px color-mix(in srgb, ${p.accent} 20%, transparent), 0 6px 20px color-mix(in srgb, ${p.accent} 22%, transparent)`
                            : "none",
                          opacity: isActive ? 1 : 0.55,                                  // dim non-selected to make active unmistakable
                          transition: "all 220ms ease",
                        }}
                      >
                        <span
                          className="w-9 h-9 rounded-lg grid place-items-center overflow-hidden p-1 shrink-0"
                          style={{
                            background: "rgba(0,0,0,0.6)",
                            border: `1px solid ${isActive ? p.accent : "color-mix(in srgb, var(--brand) 18%, transparent)"}`,
                          }}
                        >
                          <img src={p.logo} alt="" aria-hidden="true" className="w-full h-full object-contain" draggable={false} />
                        </span>
                        <span
                          className="text-[12px] font-medium leading-tight"
                          style={{ color: isActive ? "var(--text-1)" : "var(--text-2)" }}
                        >
                          {p.name}
                        </span>
                        {isActive && (
                          <Check size={12} strokeWidth={3} className="ml-auto shrink-0" style={{ color: p.accent }} />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* MIDDLE — rule highlights with green checkmarks.
                Mobile order: comes AFTER the price card (user wants to
                see pricing right under their platform pick, before the
                rule detail). Desktop layout unchanged via lg:order-2. */}
            <div
              data-testid="challenge-rules-col"
              className="order-3 lg:order-2 lg:col-span-4 card-beam p-5 lg:p-7 rounded-2xl flex flex-col lg:min-h-[560px]"
              style={{ border: "1px solid color-mix(in srgb, var(--brand) 16%, transparent)" }}
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--text-3)] font-semibold mb-4">
                {t("builder.rule_highlights", "Rule highlights")}
              </div>
              <AnimatePresence mode="wait">
                <motion.ul
                  key={program}
                  className="space-y-1"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.16 }}
                >
                  {summary.map((row, idx) => (
                    <motion.li
                      key={row.label}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.18, delay: idx * 0.035 }}
                      className="flex items-center justify-between gap-3 py-2.5 border-b border-[color:var(--line)]/40 last:border-b-0"
                    >
                      <span className="flex items-center gap-3 text-[color:var(--text-2)]">
                        <span
                          className="w-9 h-9 rounded-lg grid place-items-center shrink-0"
                          style={{
                            background: "rgba(80,220,130,0.10)",
                            border: "1px solid rgba(80,220,130,0.28)",
                          }}
                        >
                          <row.Icon size={15} strokeWidth={2.2} style={{ color: "#54d97c" }} />
                        </span>
                        <span className="leading-tight">
                          <span className="block text-[14px] font-medium text-[color:var(--text-1)]">{row.label}</span>
                          {row.note && (
                            <span className="block text-[12px] text-[color:var(--text-3)] font-light mt-0.5">{row.note}</span>
                          )}
                        </span>
                      </span>
                      <span className="font-num font-semibold text-[15px] text-[color:var(--text-1)] tabular-nums shrink-0">
                        {row.value}
                      </span>
                    </motion.li>
                  ))}
                </motion.ul>
              </AnimatePresence>

              {/* "What's included" perks — 3 program-specific trust
                  callouts at the bottom of the rules card. Filling
                  the empty space below the rule list while delivering
                  extra signal (allowed strategies, payout cadence). */}
              <div className="mt-auto pt-4 border-t border-[color:var(--line)]/40" data-testid="program-perks">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--text-3)] font-semibold mb-3 flex items-center gap-2">
                  {t("builder.whats_included", "What's also included")}
                  <button
                    type="button"
                    onClick={() => setRulesExpanded((v) => !v)}
                    className="text-[10px] uppercase tracking-[0.22em] text-brand font-semibold hover:underline inline-flex items-center gap-1"
                    data-testid="toggle-full-rules"
                  >
                    {rulesExpanded ? t("builder.hide_full_rules", "hide full rules") : t("builder.view_full_rules", "view full rules")}
                    <ChevronDown
                      size={11}
                      className={`transition-transform ${rulesExpanded ? "rotate-180" : ""}`}
                    />
                  </button>
                </div>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`perks-${program}`}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="grid grid-cols-2 gap-2"
                  >
                    {programPerks.map((perkKey) => (
                      <div
                        key={perkKey}
                        className="rounded-xl px-3 py-2.5 flex items-center gap-2 min-h-[57px]"
                        style={{
                          background: "var(--surface-2)",
                          border: "1px solid color-mix(in srgb, var(--brand) 14%, transparent)",
                        }}
                      >
                        <span
                          className="w-7 h-7 rounded-md grid place-items-center shrink-0"
                          style={{
                            background: "color-mix(in srgb, var(--brand) 12%, transparent)",
                            border: "1px solid color-mix(in srgb, var(--brand) 28%, transparent)",
                          }}
                        >
                          <Check size={13} className="text-brand" strokeWidth={2.8} />
                        </span>
                        <span className="text-[11.5px] text-[color:var(--text-1)] leading-tight font-medium">{t(perkKey)}</span>
                      </div>
                    ))}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* RIGHT — flashy price card that ADOPTS the selected
                platform's accent colour. The card glow, build summary
                eyebrow, logo halo, discount chip and CTA all shift
                hue when the user picks a different platform — so the
                selection is unmistakable.
                Content is split into 3 zones with `justify-between`
                so the card fills its height instead of bunching at
                the top. */}
            <div
              data-testid="challenge-price-col"
              className="order-2 lg:order-3 lg:col-span-4 relative p-5 lg:p-7 rounded-2xl flex flex-col overflow-hidden lg:min-h-[560px]"
              style={{
                background: `linear-gradient(135deg, color-mix(in srgb, ${activePlatform.accent} 14%, var(--surface)) 0%, var(--surface) 60%, color-mix(in srgb, ${activePlatform.accentDeep} 16%, var(--surface)) 100%)`,
                border: `1px solid color-mix(in srgb, ${activePlatform.accent} 38%, transparent)`,
                boxShadow: `inset 0 1px 0 color-mix(in srgb, ${activePlatform.accent} 14%, transparent), 0 8px 26px color-mix(in srgb, ${activePlatform.accent} 14%, transparent)`,
                transition: "background 320ms ease, border-color 320ms ease, box-shadow 320ms ease",
              }}
            >
              {/* Decorative scan grid */}
              <div
                aria-hidden
                className="absolute inset-0 opacity-[0.16] pointer-events-none"
                style={{
                  backgroundImage:
                    `linear-gradient(0deg, transparent 95%, color-mix(in srgb, ${activePlatform.accent} 55%, transparent) 100%), linear-gradient(90deg, transparent 95%, color-mix(in srgb, ${activePlatform.accent} 35%, transparent) 100%)`,
                  backgroundSize: "22px 22px",
                  transition: "background-image 320ms ease",
                }}
              />
              {/* Platform-coloured glow blob (subtle) */}
              <div
                aria-hidden
                className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none"
                style={{
                  background: `radial-gradient(circle, color-mix(in srgb, ${activePlatform.accent} 22%, transparent) 0%, transparent 70%)`,
                  transition: "background 320ms ease",
                }}
              />

              {/* ===== TOP zone — eyebrow + BIG platform logo + name ===== */}
              <div className="relative">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div
                    className="text-[10px] uppercase tracking-[0.22em] font-semibold"
                    style={{ color: activePlatform.accent }}
                  >
                    {t("builder.your_build", "Your build")}
                  </div>
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                    style={{
                      background: "rgba(80,220,130,0.16)",
                      color: "#54d97c",
                      border: "1px solid rgba(80,220,130,0.35)",
                    }}
                  >
                    <Zap size={9} strokeWidth={2.6} /> {t("builder.live_price", "Live price")}
                  </span>
                </div>

                {/* BIG platform logo + name — the dominant visual proof
                    of "what am I selecting?". Crossfades on switch. */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={platformId}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.22 }}
                    className="flex items-center gap-4"
                  >
                    <div
                      className="w-16 h-16 rounded-2xl grid place-items-center overflow-hidden p-2 shrink-0"
                      style={{
                        background: "rgba(0,0,0,0.55)",
                        border: `1.5px solid ${activePlatform.accent}`,
                        boxShadow: `0 0 0 2px color-mix(in srgb, ${activePlatform.accent} 14%, transparent), 0 6px 16px color-mix(in srgb, ${activePlatform.accent} 16%, transparent)`,
                      }}
                    >
                      <img src={activePlatform.logo} alt={activePlatform.name} className="w-full h-full object-contain" draggable={false} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display text-2xl lg:text-3xl font-medium text-[color:var(--text-1)] leading-tight">
                        {activePlatform.name}
                      </div>
                      <div className="text-[11px] text-[color:var(--text-3)] tracking-wide uppercase font-mono mt-0.5">
                        {t("builder.selected_platform", "Selected platform")}
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>

                <div className="mt-4 text-sm text-[color:var(--text-2)] font-light flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-[color:var(--text-1)]">{sizeKey(size)}</span>
                  <span className="opacity-40">·</span>
                  <span>{active.label}</span>
                </div>
              </div>

              {/* ===== ADD-ONS — applies to ALL 3 programs ===== */}
              <div className="relative mt-4" data-testid="addons-block">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--text-3)] font-semibold mb-2.5 flex items-center gap-1.5">
                  <Plus size={11} strokeWidth={2.5} /> {t("builder.addons_label", "Add-ons")}
                </div>
                <div className="space-y-2">
                  {ADDONS.map((a) => {
                    const on = addons[a.id];
                    const uplift = Math.round(basePrice * a.pct);
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => toggleAddon(a.id)}
                        role="switch"
                        aria-checked={on ? "true" : "false"}
                        aria-label={`Toggle ${a.label} add-on`}
                        data-testid={`addon-${a.id}`}
                        className="w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-colors active:scale-[0.99]"
                        style={{
                          background: on
                            ? `color-mix(in srgb, ${activePlatform.accent} 14%, var(--surface-2))`
                            : "var(--surface-2)",
                          border: on
                            ? `1px solid color-mix(in srgb, ${activePlatform.accent} 50%, transparent)`
                            : "1px solid var(--line)",
                        }}
                      >
                        <span
                          className="w-9 h-9 rounded-lg grid place-items-center shrink-0 transition-colors"
                          style={{
                            background: on
                              ? `color-mix(in srgb, ${activePlatform.accent} 26%, transparent)`
                              : "color-mix(in srgb, var(--brand) 6%, transparent)",
                            border: on
                              ? `1px solid ${activePlatform.accent}`
                              : "1px solid color-mix(in srgb, var(--brand) 18%, transparent)",
                          }}
                        >
                          <a.Icon size={15} style={{ color: on ? activePlatform.accent : "var(--text-2)" }} strokeWidth={2.2} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[13px] font-medium text-[color:var(--text-1)] leading-tight">{a.label}</span>
                          <span className="block text-[12px] text-[color:var(--text-3)] font-light truncate">{a.blurb}</span>
                        </span>
                        <span className="font-mono font-semibold text-[12px] tabular-nums shrink-0" style={{ color: on ? activePlatform.accent : "var(--text-3)" }}>
                          +${uplift}
                        </span>
                        {/* Toggle switch */}
                        <span
                          className="relative w-9 h-5 rounded-full shrink-0 transition-colors"
                          style={{
                            background: on ? activePlatform.accent : "var(--line-strong)",
                          }}
                        >
                          <span
                            className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform"
                            style={{ transform: on ? "translateX(18px)" : "translateX(2px)" }}
                          />
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ===== MIDDLE zone — price ===== */}
              <div className="relative my-auto py-6">
                <div className="flex items-baseline gap-3 mb-1">
                  <span
                    className="font-num text-5xl lg:text-[64px] font-medium tabular-nums leading-none"
                    style={{
                      backgroundImage: `linear-gradient(135deg, ${activePlatform.accent} 0%, ${activePlatform.accentDeep} 100%)`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    <AnimatedPrice value={currentPrice} />
                  </span>
                </div>
                <div className="text-[11px] text-[color:var(--text-3)] uppercase tracking-widest">
                  {t("builder.one_time", "One-time · Lifetime account")}
                </div>
              </div>

              {/* ===== BOTTOM zone — CTA + trust line ===== */}
              <div className="relative">
                <a
                  href={buyUrl(program, size, platformId)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`buy-btn-${platformId}`}
                  className="w-full inline-flex items-center justify-center gap-2 h-14 px-4 rounded-2xl font-display text-sm sm:text-base font-semibold transition-transform hover:scale-[1.02] active:scale-[0.99] overflow-hidden"
                  style={{
                    background: `linear-gradient(135deg, ${activePlatform.accent} 0%, ${activePlatform.accentDeep} 100%)`,
                    color: "#071116",
                    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.30), 0 4px 14px color-mix(in srgb, ${activePlatform.accent} 25%, transparent)`,
                    transition: "background 320ms ease, box-shadow 320ms ease, transform 160ms ease",
                  }}
                >
                  <span className="truncate">{t("builder.start_on", "Start on {{platform}}", { platform: activePlatform.name })}</span>
                  <ArrowRight size={18} strokeWidth={2.5} className="shrink-0" />
                </a>

                {/* Tiny inline reminder — sits just under the CTA so it
                    catches the eye as soon as they're about to click. */}
                <p
                  className="mt-2 text-[12px] leading-snug font-light text-center text-[color:var(--text-3)]"
                  data-testid="checkout-addons-reminder"
                >
                  {t("challenges.reselect_addons", "Re-select your add-ons on the checkout page — they don't carry over.")}
                </p>

                <div className="mt-4 flex items-center justify-center gap-3 text-[10px] text-[color:var(--text-3)] font-mono uppercase tracking-wider">
                  <span className="flex items-center gap-1"><Check size={10} className="text-brand" /> {t("challenges.no_kyc", "No KYC to start")}</span>
                  <span className="opacity-30">·</span>
                  <span className="flex items-center gap-1"><Check size={10} className="text-brand" /> {t("challenges.instant_funded", "Instant funded")}</span>
                </div>
              </div>
            </div>
          </div>

        {/* Full rules table — collapsed by default, expandable */}
        <AnimatePresence>
          {rulesExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden mt-6"
            >
              <RulesTable program={program} active={active} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

function RulesTable({ program, active }) {
  const { t } = useTranslation();
  const isTwoStep = program === "twoStep";
  const isOneStep = program === "oneStep";
  return (
    <div className="card-beam overflow-hidden rounded-2xl" data-testid="rules-table">
      <div className="px-5 lg:px-7 py-5 border-b border-[color:var(--line)] flex items-center gap-3">
        <div className="w-1.5 h-6 bg-brand rounded-full" />
        <h3 className="font-display text-lg font-medium text-[color:var(--text-1)]">{t("challenges.rules_table_title", "Trading Rules — {{plan}}", { plan: active.label })}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--line)]">
            <tr className="text-[color:var(--text-3)] font-mono text-[11px] uppercase tracking-wider">
              <th className="text-left px-5 lg:px-7 py-4">{t("challenges.rule_area", "Rule Area")}</th>
              {isTwoStep && (<>
                <th className="text-left px-5 py-4">{t("challenges.step1", "Step 1")}</th>
                <th className="text-left px-5 py-4">{t("challenges.step2", "Step 2")}</th>
              </>)}
              {isOneStep && <th className="text-left px-5 py-4">{t("challenges.assessment", "Assessment")}</th>}
              <th className="text-left px-5 py-4">{t("challenges.funded", "Funded")}</th>
              <th className="text-left px-5 lg:px-7 py-4 hidden md:table-cell">{t("challenges.notes", "Notes")}</th>
            </tr>
          </thead>
          <tbody>
            {active.rules.map((r) => (
              <tr key={r.area} className="border-t border-[color:var(--line)] hover:bg-[color:var(--line)]/50 transition-colors">
                <td className="px-5 lg:px-7 py-4 font-display text-[color:var(--text-1)] text-sm font-medium">{r.area}</td>
                {isTwoStep && (<>
                  <td className="px-5 py-4 font-num text-[color:var(--text-2)]">{r.step1}</td>
                  <td className="px-5 py-4 font-num text-[color:var(--text-2)]">{r.step2}</td>
                </>)}
                {isOneStep && <td className="px-5 py-4 font-num text-[color:var(--text-2)]">{r.assessment}</td>}
                <td className="px-5 py-4 font-num text-brand font-medium">{r.funded}</td>
                <td className="px-5 lg:px-7 py-4 text-[color:var(--text-3)] text-xs hidden md:table-cell">{r.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
